/**
 * ⚖ **S1 — the SPA's last-resort display name. A MIRROR, not a new idea.**
 *
 * ## Why this file exists
 *
 * Rule 6 says *"no file is ever named by its OS name"*, and the AGENT plane has
 * obeyed it for months via `extensions/lexiun-core/src/display/derive-display-name.ts`.
 * The user plane never could: every LexIDE surface already consults the display
 * overlay (`title` → `alias` → `tag`) and then, having nothing else, falls back
 * to the raw basename. Three separate surfaces carried comments asserting the
 * rule beside code that broke it — including a chat chip that suppressed its
 * tooltip "to avoid leaking the OS name" while putting the OS name in its label.
 *
 * There was exactly ONE tier missing, shared by all of them. This is that tier.
 * Analysis: `ai-docs/lexide-workspace-display-everywhere-analysis.md`.
 *
 * ## ⚠ This is a MIRROR and must stay one
 *
 * The runtime file is the SOURCE OF TRUTH. This exists only because the SPA
 * cannot import across that package boundary — the same constraint that already
 * forces the intake harnesses to replicate it (the `source-info.ts` precedent).
 * `derive-display-name.parity.test.ts` reads the runtime file from disk and
 * fails when the vocabulary or the machine-name shape drift apart. **If you edit
 * this file, edit the runtime one, or the parity test will tell you.**
 *
 * ⚠ **Scope: the ALIAS path only.** Tag derivation (`deriveTag*`) is NOT
 * mirrored — tags are minted, never derived on read, and a SPA-side tag deriver
 * would invent handles that resolve to nothing.
 *
 * ## What this is NOT
 *
 * Not an identity. A derived name changes when the deriver changes, so it is a
 * LABEL and nothing may key on it — no `@handle` target, no search key, no
 * cross-user reference. That is the read/target criterion the analysis sets and
 * `lexiun-soul` adopted into the DS-34/35 vocabulary.
 */

/** pt-BR labels for the controlled ingest doctype vocabulary.
 *  ⚠ MIRRORED from the runtime — pinned by the parity test. */
export const DOCTYPE_LABELS_PT: Record<string, string> = {
  contestacao: "Contestação",
  "peticao-inicial": "Petição inicial",
  peticao: "Petição",
  replica: "Réplica",
  treplica: "Tréplica",
  "laudo-medico": "Laudo médico",
  laudo: "Laudo",
  "nota-fiscal": "Nota fiscal",
  contrato: "Contrato",
  procuracao: "Procuração",
  sentenca: "Sentença",
  despacho: "Despacho",
  decisao: "Decisão",
  acordao: "Acórdão",
  recurso: "Recurso",
  embargos: "Embargos",
  oficio: "Ofício",
  comprovante: "Comprovante",
  audio: "Áudio",
  imagem: "Imagem",
  foto: "Foto",
  email: "E-mail",
  documento: "Documento",
};

/** De-kebab + Title Case a slug: "trend-moveis" → "Trend Moveis". */
export function humanizeSlug(slug: string): string {
  return slug
    .replace(/[-_]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .split(" ")
    .map((w) => (w ? w[0]!.toUpperCase() + w.slice(1) : w))
    .join(" ");
}

/** The pt-BR label for a doctype token (mapped, else humanized). */
export function doctypeLabel(doctype: string): string {
  return DOCTYPE_LABELS_PT[doctype.toLowerCase()] ?? humanizeSlug(doctype);
}

/** `<slug>__<doctype>__<yyyymmdd[-hhmmss]>.<ext>` — the §1.5 machine-name shape. */
export const MACHINE_NAME = /^(.+?)__(.+?)__(\d{8}(?:-\d{6})?)\.[a-z0-9]+$/i;

function stripExt(name: string): string {
  const i = name.lastIndexOf(".");
  return i > 0 ? name.slice(0, i) : name;
}

/** Case-relative zone of a workspace path: the FIRST segment ("sources/x.pdf"
 *  → "sources"). Leading slashes tolerated; a bare basename yields "". */
export function zoneOfPath(path: string): string {
  const clean = path.replace(/^\/+/, "");
  const slash = clean.indexOf("/");
  return slash > 0 ? clean.slice(0, slash) : "";
}

export interface DeriveInput {
  /** Case-relative zone dir, e.g. "sources", "raw-intake", "artifacts". */
  zone: string;
  /** The on-disk basename. */
  machineName: string;
  /** Cleaned original upload filename (raw-intake/), when known. */
  originalFilename?: string;
}

/**
 * The friendly pt-BR label for a file that has NO title, alias or tag.
 *
 * Never returns empty: a pathological all-separator name falls back to the raw
 * basename, because an empty label is worse than an ugly one — it renders as a
 * blank row the lawyer cannot click or even see.
 */
export function deriveDisplayLabel(input: DeriveInput): string {
  const derived = deriveRaw(input);
  return derived.trim() || input.machineName;
}

function deriveRaw(input: DeriveInput): string {
  const { zone, machineName } = input;

  // sources/ — the frozen originals carry the `<slug>__<doctype>__<ts>` shape.
  if (zone === "sources") {
    const m = MACHINE_NAME.exec(machineName);
    if (m) {
      const [, slug, doctype] = m;
      const label = doctypeLabel(doctype!);
      const slugHuman = humanizeSlug(slug!);
      return slugHuman.toLowerCase().includes(humanizeSlug(doctype!).toLowerCase()) ||
        slugHuman === ""
        ? slugHuman || label
        : `${label} — ${slugHuman}`;
    }
    return humanizeSlug(stripExt(machineName));
  }

  // raw-intake/ — the verbatim upload: the original filename is the friendly one.
  if (zone === "raw-intake") {
    return input.originalFilename?.trim() || humanizeSlug(stripExt(machineName));
  }

  // artifacts/ + every other root — humanize the basename (sans extension).
  return humanizeSlug(stripExt(machineName));
}

/**
 * The one call every surface's terminal fallback should use.
 *
 * Takes what a tree node / chip / viewer actually has (a path and a basename)
 * rather than a pre-computed zone, so no caller has to know how a zone is
 * derived — a caller computing it by hand is how the four fallbacks drifted
 * apart in the first place.
 */
export function displayLabelForPath(
  path: string,
  basename: string,
  originalFilename?: string,
): string {
  return deriveDisplayLabel({
    zone: zoneOfPath(path),
    machineName: basename,
    originalFilename,
  });
}
